import { FC, useCallback, useMemo } from 'react';
import { Button, Card, Checkbox, RadixTable, TColumn } from '@features/ui';
import { ProductOffer } from '@src/types/productsAddons.ts';
import { TradingPlatForms } from '@src/types';
import { cn } from '@lib/utils.ts';

interface BundlesProps {
  onEdit: (id: number) => void;
  data: ProductOffer[];
  bundleSelectedIds: number[];
  setBundleSelectedIds: (values: number[]) => void;
}
export const Bundles: FC<BundlesProps> = ({
  onEdit,
  data,
  bundleSelectedIds,
  setBundleSelectedIds,
}) => {
  const handleEdit = useCallback(
    (id?: number) => {
      if (!id) return;
      onEdit(id);
    },
    [onEdit]
  );

  const handleSelectedView = useCallback(
    (id?: number) => {
      if (!id) return;
      const findId = bundleSelectedIds.find((i) => i === id);

      if (findId) {
        setBundleSelectedIds(bundleSelectedIds.filter((item) => item !== id));

        return;
      }
      setBundleSelectedIds([...bundleSelectedIds, id]);
    },
    [bundleSelectedIds, setBundleSelectedIds]
  );

  const columns = useMemo(
    (): TColumn<ProductOffer>[] => [
      {
        title: (
          <div className={'ml-10'}>
            <p>#</p>
          </div>
        ),
        className: 'w-20',
        render: (item, index) => (
          <div className={'flex gap-4 items-center'}>
            <Checkbox
              checked={bundleSelectedIds.includes(item?.id ?? 0)}
              className="size-6"
              onCheckedChange={() => handleSelectedView(item?.id)}
            />
            <p>{(index ?? 0) + 1}</p>
          </div>
        ),
      },
      {
        title: 'Title',
        render: (item) => <p>{item?.title}</p>,
      },
      {
        title: 'Accounts in bundle',
        render: (item) => <p>{item?.accounts_in_bundle}</p>,
      },
      {
        title: '',
        render: (item) => {
          const compatibleDataFeeds = item?.compatible_data_feeds ?? [];

          return (
            <div
              className={'flex gap-x-5 w-full'}
              style={{ width: compatibleDataFeeds.length * 121 }}
            >
              {compatibleDataFeeds.map((itemDataFeed) => {
                if (itemDataFeed === TradingPlatForms.CQG_DATA_FEED) {
                  return (
                    <img
                      key={itemDataFeed}
                      alt="leeloo-logo"
                      className={'w-[101px] h-[30px]'}
                      src="/assets/logo-tradovate.png"
                    />
                  );
                }
                if (itemDataFeed === TradingPlatForms.RITHMIC_DATA_FEED) {
                  return (
                    <img
                      key={itemDataFeed}
                      alt="leeloo-logo"
                      className={'w-[101px] h-[30px]'}
                      src="/assets/logo-rithmic.png"
                    />
                  );
                }

                return (
                  <img
                    key={itemDataFeed}
                    alt="leeloo-logo"
                    className={'w-[101px] h-[30px]'}
                    src="/assets/logo-dxfeed.png"
                  />
                );
              })}
            </div>
          );
        },
      },
      {
        title: 'Status',
        render: (item) => {
          return (
            <div
              className={cn(
                item?.is_active ? 'bg-lime' : 'bg-[#DC342E]',
                'h-[21px] pl-[10px] pr-[9px] w-fit flex items-center rounded-[2px] text-xs leading-[1.5]'
              )}
            >
              <p>{item?.is_active ? 'Active' : 'Inactive'}</p>
            </div>
          );
        },
      },
      {
        title: 'Visible on website',
        render: (item) => <p>{item?.is_visible_on_web ? 'Yes' : 'No'}</p>,
      },
      {
        title: '',
        render: (item) => (
          <Button
            size={'sm'}
            variant={'secondary'}
            onClick={() => handleEdit(item?.id)}
          >
            Edit
          </Button>
        ),
      },
    ],
    [bundleSelectedIds, handleEdit, handleSelectedView]
  );

  return (
    <Card className={'mt-[27px] pl-[18px] pr-[24px]'}>
      <RadixTable columns={columns} data={data} />
    </Card>
  );
};
